import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { FacilitiesFetchedAction } from '@Application/Facilities/Facilities/Actions/facilitiesFetched';
import type { FacilityCreatedAction } from '@Application/Facilities/Facilities/Actions/facilityCreated';
import type { FacilityDeletedAction } from '@Application/Facilities/Facilities/Actions/facilityDeleted';
import type { FacilityUpdatedAction } from '@Application/Facilities/Facilities/Actions/facilityUpdated';
import FacilitiesListView from '@UI/Store/Facilities/Views/FacilitiesListView';
import FacilityView from '@UI/Store/Facilities/Views/FacilityView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type FacilitiesStore = {
	facilities: FacilitiesListView | null;
};

const initialState: FacilitiesStore = {
	facilities: null,
};

type Actions =
	| FacilitiesFetchedAction
	| FacilityCreatedAction
	| FacilityUpdatedAction
	| FacilityDeletedAction
	| UserRevokedAction;

const whenFacilitiesFetched = (store: FacilitiesStore, action: FacilitiesFetchedAction): FacilitiesStore => ({
	...store,
	facilities: new FacilitiesListView(
		action.payload.map((facility) => new FacilityView(facility)),
		action.organizationId,
	),
});

const whenFacilityCreated = (store: FacilitiesStore, action: FacilityCreatedAction): FacilitiesStore => {
	if (!store.facilities || store.facilities.organizationId !== action.organizationId) {
		return store;
	}

	return {
		...store,
		facilities: store.facilities.addFacility(new FacilityView(action.payload)),
	};
};

const whenFacilityUpdated = (store: FacilitiesStore, action: FacilityUpdatedAction): FacilitiesStore => {
	if (!store.facilities || store.facilities.organizationId !== action.organizationId) {
		return store;
	}

	return {
		...store,
		facilities: store.facilities.updateFacility(new FacilityView(action.payload)),
	};
};

const whenFacilityDeleted = (store: FacilitiesStore, action: FacilityDeletedAction): FacilitiesStore => {
	if (!store.facilities || store.facilities.organizationId !== action.organizationId) {
		return store;
	}

	return {
		...store,
		facilities: store.facilities.removeFacility(action.payload.facilityId),
	};
};

const whenUserRevoked = (): FacilitiesStore => initialState;

export default registerStore<'facilitiesStore', Actions>(
	'facilitiesStore',
	createSliceReducer({
		FACILITIES_FETCHED: whenFacilitiesFetched,
		FACILITY_CREATED: whenFacilityCreated,
		FACILITY_UPDATED: whenFacilityUpdated,
		FACILITY_DELETED: whenFacilityDeleted,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
